import { OverlayRef } from '@angular/cdk/overlay';
import { Observable, ReplaySubject, Subject } from 'rxjs';

/**
 * Reference to a dialog instance opened by HxDialogService
 *
 * @template T - Type of the component loaded into the dialog
 * @template R - Type of the result value returned when dialog closes
 */
export class HxDialogRef<T = any, R = any> {
  /** Instance of the component loaded into the dialog */
  readonly componentInstance: T;

  /** Unique ID for this dialog instance */
  readonly id: string;

  private readonly _afterOpened = new ReplaySubject<void>(1);
  private readonly _afterClosed = new Subject<R | undefined>();
  private readonly _beforeClosed = new Subject<R | undefined>();

  private _result: R | undefined;

  constructor(
    private _overlayRef: OverlayRef,
    componentInstance: T,
    id: string
  ) {
    this.componentInstance = componentInstance;
    this.id = id;
  }

  /**
   * Close the dialog
   *
   * @param result Optional result value to return to caller
   */
  close(result?: R): void {
    this._result = result;
    this._beforeClosed.next(result);
    this._beforeClosed.complete();

    // Trigger close animation, then dispose overlay
    setTimeout(() => {
      this._afterClosed.next(result);
      this._afterClosed.complete();
      this._overlayRef.dispose();
    }, 250); // Match animation duration
  }

  /** Observable that emits when the dialog has opened */
  afterOpened(): Observable<void> {
    return this._afterOpened.asObservable();
  }

  /** Observable that emits when the dialog has closed with result value */
  afterClosed(): Observable<R | undefined> {
    return this._afterClosed.asObservable();
  }

  /** Observable that emits before the dialog close animation starts */
  beforeClosed(): Observable<R | undefined> {
    return this._beforeClosed.asObservable();
  }

  /** Observable that emits when the backdrop is clicked */
  backdropClick(): Observable<MouseEvent> {
    return this._overlayRef.backdropClick();
  }

  /** Observable that emits keyboard events from the dialog */
  keydownEvents(): Observable<KeyboardEvent> {
    return this._overlayRef.keydownEvents();
  }

  /** Internal method to mark dialog as opened */
  _notifyOpened(): void {
    this._afterOpened.next();
    this._afterOpened.complete();
  }
}
